import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import ContactCTA from '../components/ContactCTA';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/schema';
import { Sparkles, CalendarCheck, ShieldCheck, Star, Smartphone, Search, Users, Instagram } from 'lucide-react';

const faqs = [
  { q: 'How much does a med spa website cost?', a: 'Custom med spa websites at Website Symphony typically range from $2,500 to $8,500 depending on the number of services, integrations (online booking, HIPAA-compliant forms, gift cards, memberships), and branding depth. Every project includes a discovery call and fixed-price quote before any work begins.' },
  { q: 'Do you build HIPAA-compliant intake forms?', a: 'Yes. We integrate HIPAA-compliant intake, consent and consultation forms through vetted platforms so patient information is captured securely and stored in a compliant environment.' },
  { q: 'Can you connect my booking software (Vagaro, Boulevard, Mindbody, Aesthetic Record)?', a: 'Absolutely. We regularly integrate Vagaro, Boulevard, Mindbody, Aesthetic Record, Symplast, Nextech, and custom booking platforms so clients can book right from your website.' },
  { q: 'Will my site rank on Google for local searches like "med spa near me"?', a: 'Every med spa site we build ships with local SEO baked in: schema markup, Google Business Profile alignment, service-area pages, treatment landing pages, and page-speed optimization to help you show up for high-intent searches.' },
  { q: 'How long does it take to launch a med spa website?', a: 'Most med spa websites launch in 3–5 weeks from kickoff. Sites with custom membership portals or complex integrations may take 6–8 weeks.' },
];

const treatments = ['Botox & Dysport', 'Dermal Fillers', 'Laser Hair Removal', 'Chemical Peels', 'Microneedling & PRP', 'CoolSculpting / Body Contouring', 'IV Therapy', 'Medical Weight Loss', 'Facials & Skincare', 'Hormone Therapy'];

const features = [
  { icon: CalendarCheck, title: 'Online Booking Integration', desc: 'Vagaro, Boulevard, Mindbody, Aesthetic Record and Symplast — seamlessly embedded.' },
  { icon: ShieldCheck, title: 'HIPAA-Aware Forms', desc: 'Secure intake, consultation and consent forms that protect patient data.' },
  { icon: Star, title: 'Before & After Galleries', desc: 'Beautiful, tastefully-tagged galleries with lightbox filtering by treatment.' },
  { icon: Sparkles, title: 'Membership & Package Pages', desc: 'Recurring revenue built in — memberships, VIP tiers, gift cards and packages.' },
  { icon: Smartphone, title: 'Mobile-First Design', desc: 'Over 70% of med spa traffic is mobile. Your site loads in under 2 seconds on 4G.' },
  { icon: Search, title: 'Local SEO Built-in', desc: 'Treatment pages, service-area pages and schema so you rank for "botox near me".' },
];

const MedSpaPage = () => {
  const schema = combineSchemas(
    organizationSchema,
    serviceSchema('Web Design for Med Spas', 'Custom web design for medical spas including online booking integration, HIPAA-aware forms, treatment landing pages and local SEO.', '/web-design-for-med-spas'),
    faqSchema(faqs),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Web Design for Med Spas', path: '/web-design-for-med-spas' }]),
  );

  return (
    <Layout>
      <SEO
        title="Web Design for Med Spas | Custom Med Spa Websites | Website Symphony"
        description="Beautiful, conversion-focused web design for med spas. Online booking, HIPAA-aware forms, before & after galleries and local SEO. Free quote from Website Symphony."
        keywords="web design for med spas, med spa web design, medical spa website design, medspa website, botox website design, aesthetic clinic web design, HIPAA compliant medical website"
        canonical="/web-design-for-med-spas"
        schema={schema}
      />
      <PageHero title="Web Design for Med Spas" subtitle="Luxurious, patient-first websites that fill your booking calendar — with online scheduling, HIPAA-aware forms and treatment pages built to rank." breadcrumb="Med Spa Web Design" />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-6">Med spa websites that actually book appointments</h2>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">
              Your med spa website should do more than look pretty — it should convert curious visitors into booked consultations. At Website Symphony, we design med spa websites around one goal: <strong>filling your appointment calendar with high-value clients</strong>.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">
              Every site we build for a medical spa is designed to feel like your best treatment room — clean, calm, and premium — while behind the scenes it’s working hard: capturing leads with HIPAA-aware forms, showing your before-and-after work through elegant galleries, and helping you rank on Google for local, high-intent searches like <em>“botox near me”</em> or <em>“best med spa in Miami”</em>.
            </p>
            <p className="text-gray-600 text-[15px] leading-relaxed">Whether you’re a single-provider aesthetic clinic or a multi-location medical spa group, we’ll build a site that grows with you.</p>
          </div>
          <img src="https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=900&q=80" alt="Modern med spa treatment room" className="rounded-lg shadow-xl w-full h-[440px] object-cover" loading="lazy" />
        </div>
      </section>

      <section className="py-20 bg-[#F7F7F5]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold text-center mb-14">Everything a modern med spa website needs</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-7 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#D9A04A]/15 flex items-center justify-center mb-4">
                  <f.icon className="text-[#D9A04A]" size={22} />
                </div>
                <h3 className="text-[#2C5F66] font-bold text-lg mb-2">{f.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold text-center mb-4">Purpose-built treatment landing pages</h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-10">Each treatment gets its own SEO-optimized landing page, so patients searching for a specific service find you — not your competitor.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {treatments.map((t) => (
              <span key={t} className="px-5 py-2.5 bg-[#2C5F66] text-white rounded-full text-sm font-medium hover:bg-[#D9A04A] transition-colors cursor-default">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <FAQ items={faqs} title="Med Spa Web Design FAQs" />
      <ContactCTA defaultService="Med Spa Web Design" heading="Ready to fill your booking calendar?" subheading="Get a free med spa website consultation and estimate — no obligation." />
    </Layout>
  );
};

export default MedSpaPage;
